import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FileUp, Plus, Trash2 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { getErrorMessage } from '../../../lib/apiClient';
import {
  createDocument,
  replaceFile,
  replaceSigners,
  signatureKeys,
  updateDocument,
} from '../signaturesApi';
import { signerColor } from '../lib/fieldTypes';

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const MAX_SIGNERS = 5;

/**
 * Wizard step 1 — upload the PDF, name the document, and (for agreements) list
 * up to five recipients. Mirrors OtterWaiver's "PDF Upload" step.
 */
export default function StepUpload({ type, document: doc, signers, onDone }) {
  const isAgreement = type === 'agreement';
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const form = useForm({
    defaultValues: {
      title: doc?.title ?? '',
      expiresAt: doc?.expiresAt ? doc.expiresAt.slice(0, 10) : '',
      signers: signers?.length
        ? signers.map((s) => ({
            firstName: s.firstName,
            lastName: s.lastName,
            email: s.email,
          }))
        : [{ firstName: '', lastName: '', email: '' }],
    },
  });
  const { fields: signerRows, append, remove } = useFieldArray({
    control: form.control,
    name: 'signers',
  });

  const save = useMutation({
    mutationFn: async (values) => {
      const expiresAt = values.expiresAt ? new Date(values.expiresAt).toISOString() : undefined;
      let document = doc;
      if (!document) {
        if (!file) throw new Error('Choose a PDF to continue');
        document = await createDocument({ file, title: values.title, type, expiresAt });
      } else {
        if (file) {
          document = await replaceFile({ id: document.id, file });
          // The source-PDF query is cached with staleTime: Infinity — drop it so
          // step 2 downloads the replacement instead of the old bytes.
          queryClient.removeQueries({ queryKey: signatureKeys.sourcePdf(document.id) });
        }
        document = await updateDocument({
          id: document.id,
          title: values.title,
          ...(values.expiresAt ? { expiresAt } : {}),
        });
      }
      if (isAgreement) {
        const emails = values.signers.map((s) => s.email.trim().toLowerCase());
        if (new Set(emails).size !== emails.length) {
          throw new Error('Each recipient needs a distinct email');
        }
        await replaceSigners({ id: document.id, signers: values.signers });
      }
      return document;
    },
    onSuccess: (document) => onDone(document),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const acceptFile = (candidate) => {
    if (!candidate) return;
    if (candidate.type !== 'application/pdf') {
      toast.error('Only PDF files can be sent for signature');
      return;
    }
    setFile(candidate);
    if (!form.getValues('title')) {
      form.setValue('title', candidate.name.replace(/\.pdf$/i, ''));
    }
  };

  return (
    <form
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      className="mx-auto max-w-2xl space-y-6"
    >
      {/* PDF dropzone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          acceptFile(e.dataTransfer.files?.[0]);
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          dragOver ? 'border-brand-600 bg-brand-600/5' : 'border-slate-300 bg-slate-100'
        }`}
      >
        <FileUp className="h-8 w-8 text-slate-400" />
        <p className="text-sm text-slate-600">
          {file ? (
            <span className="font-medium text-slate-900">{file.name}</span>
          ) : doc ? (
            <>
              Current PDF kept ({doc.pagesInPdf} page{doc.pagesInPdf === 1 ? '' : 's'}) — drop a
              new one to replace it
            </>
          ) : (
            'Drag a PDF here, or browse'
          )}
        </p>
        <label className="cursor-pointer rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">
          Browse files
          <input
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={(e) => {
              acceptFile(e.target.files?.[0]);
              // Allow re-selecting the same filename to fire another change event.
              e.target.value = '';
            }}
          />
        </label>
        {doc && file && (
          <p className="text-xs text-amber-500">
            Replacing the PDF clears any fields already placed.
          </p>
        )}
      </div>

      <Input
        label="Document title"
        placeholder="Mutual NDA"
        error={form.formState.errors.title?.message}
        {...form.register('title', { required: 'Give the document a title', maxLength: 255 })}
      />

      <Input
        label="Expiration date (optional)"
        type="date"
        hint="Signing links stop working after this date"
        {...form.register('expiresAt')}
      />

      {isAgreement && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Recipients</h3>
            {signerRows.length < MAX_SIGNERS && (
              <button
                type="button"
                onClick={() => append({ firstName: '', lastName: '', email: '' })}
                className="flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline"
              >
                <Plus className="h-4 w-4" /> Add recipient
              </button>
            )}
          </div>
          {signerRows.map((row, index) => (
            <div
              key={row.id}
              className="rounded-xl border border-slate-200 bg-slate-100 p-3"
              style={{ borderLeftWidth: 4, borderLeftColor: signerColor(index) }}
            >
              <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1.4fr_auto]">
                <Input
                  placeholder="First name"
                  aria-label={`Recipient ${index + 1} first name`}
                  error={form.formState.errors.signers?.[index]?.firstName?.message}
                  {...form.register(`signers.${index}.firstName`, { required: 'Required' })}
                />
                <Input
                  placeholder="Last name"
                  aria-label={`Recipient ${index + 1} last name`}
                  {...form.register(`signers.${index}.lastName`)}
                />
                <Input
                  placeholder="email@example.com"
                  aria-label={`Recipient ${index + 1} email`}
                  error={form.formState.errors.signers?.[index]?.email?.message}
                  {...form.register(`signers.${index}.email`, {
                    required: 'Required',
                    pattern: { value: EMAIL_RE, message: 'Invalid email' },
                  })}
                />
                <button
                  type="button"
                  aria-label="Remove recipient"
                  disabled={signerRows.length === 1}
                  onClick={() => remove(index)}
                  className="self-center rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-red-500 disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          <p className="text-xs text-slate-400">
            Each recipient receives their own signing link by email when you publish.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          Continue to field placement
        </Button>
      </div>
    </form>
  );
}
